import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ArrowBack } from "@mui/icons-material";
import { Avatar } from "@mui/material";
import Logo from "../assets/images/commaLogo.png";
import { LogOut } from "lucide-react";

const CreateSpeechAssetCard = ({ onAssetCreated }) => {
  const [assetName, setAssetName] = useState("");
  const [caption, setCaption] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState("");
  const [user, setUser] = useState(null);
  const [ttsLoading, setTTSLoading] = useState(false); // Separate loading for TTS generation
  // --- Added for upload/record ---
  const [mode, setMode] = useState("tts");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recorder, setRecorder] = useState(null);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [uploadMode, setUploadMode] = useState("upload"); // "upload" or "record"

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    console.log("Token being sent:", token);

    axios
      .get("https://genz-staging.feeltiptop.com/api/user/getCurrentUserInfo", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        console.log("User info response:", response.data);
        setUser(response.data.username);
      })
      .catch((error) => {
        console.error("Error fetching user info:", error);
        if (error.response) {
          console.error("Response data:", error.response.data);
          console.error("Response status:", error.response.status);
          console.error("Response headers:", error.response.headers);
        }
      });
  }, []);

  // ---- File/record helpers ----
  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("audio/")) {
      setError("Please upload a valid audio file.");
      return;
    }
    setError("");
    setSelectedFile(file);
    setRecordedBlob(null);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (!file) return;
    if (!file.type.startsWith("audio/")) {
      setError("Please drop a valid audio file.");
      return;
    }
    setError("");
    setSelectedFile(file);
    setRecordedBlob(null);
  };

  const toggleRecording = async () => {
    if (isRecording) {
      recorder.stop();
      setIsRecording(false);
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mediaRecorder = new window.MediaRecorder(stream);
        let chunks = [];
        mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
        mediaRecorder.onstop = () => {
          const blob = new Blob(chunks, { type: 'audio/webm' });
          setRecordedBlob(blob);
          stream.getTracks().forEach(track => track.stop());
        };
        mediaRecorder.start();
        setRecorder(mediaRecorder);
        setIsRecording(true);
      } catch (err) {
        console.error("Error accessing microphone:", err);
        setError("Microphone access denied.");
      }
    }
  };

  const writeString = (view, offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  const encodeWAV = (audioBuffer) => {
    const numChannels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const bitDepth = 16;
    const samples = audioBuffer.getChannelData(0);
    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);
    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + samples.length * 2, true);
    writeString(view, 8, 'WAVE');
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * numChannels * bitDepth / 8, true);
    view.setUint16(32, numChannels * bitDepth / 8, true);
    view.setUint16(34, bitDepth, true);
    writeString(view, 36, 'data');
    view.setUint32(40, samples.length * 2, true);
    let offset = 44;
    for (let i = 0; i < samples.length; i++) {
      const s = Math.max(-1, Math.min(1, samples[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
      offset += 2;
    }
    return view;
  };

  const fileToWavBase64 = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const decoded = await audioContext.decodeAudioData(arrayBuffer);
    const wavBuffer = encodeWAV(decoded);
    const blob = new Blob([wavBuffer], { type: 'audio/wav' });
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  // ---- End helpers ----

  const generateTTSAndSave = async () => {
    if (!assetName.trim() || !caption.trim()) {
      setError("Please provide both an Asset Name and Caption.");
      return;
    }

    if (isCreating) {
      setError("Asset creation in progress. Please wait.");
      return;
    }

    setError("");
    setIsCreating(true);
    setTTSLoading(true); // Start TTS loading

    // -------- Get or create Brand --------
    let brandId = null;
    try {
      const token = localStorage.getItem("authToken");
      // Check if the user already has a brand
      const brandsResponse = await axios.get(
        "https://genz-staging.feeltiptop.com/api/brand/getUserBrands",
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (Array.isArray(brandsResponse.data) && brandsResponse.data.length > 0) {
        // backend returns list of tuples -> [[BrandID], …]
        brandId = Array.isArray(brandsResponse.data[0])
          ? brandsResponse.data[0][0]
          : brandsResponse.data[0].BrandID ?? brandsResponse.data[0];
      } else {
        // No existing brand – create one
        const addResp = await axios.post(
          "https://genz-staging.feeltiptop.com/api/brand/addBrand",
          { BrandName: `${user} - Brand` },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        brandId = addResp.data.brand_id;
      }
    } catch (brandErr) {
      console.error("Error fetching/creating brand:", brandErr);
      setError("Failed to get or create Brand. Please try again.");
      setIsCreating(false);
      setTTSLoading(false);
      return;
    }
    // -------------------------------------

    let audioBase64 = null;

    // 1. Generate TTS Audio
    try {
      const ttsResponse = await axios.get(
        `https://feeltiptop.com/demos/videogen/MSSpeech.php?usetiptoptts=1&txt=${encodeURI(caption)}`,
        { responseType: "blob" }
      );
      const audioBlob = new Blob([ttsResponse.data], { type: "audio/wav" });

      // Convert blob to base64
      audioBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(audioBlob);
      });

    } catch (ttsError) {
      console.error("Error fetching TTS Audio:", ttsError);
      setError("Failed to generate audio. Please try again.");
      setIsCreating(false);
      setTTSLoading(false);
      return;
    } finally {
      setTTSLoading(false); // Stop TTS loading regardless of outcome
    }

    // 2. Save the Asset if TTS was successful
    if (audioBase64) {
      try {
        const preContent = {
          assetType: "SpeechAsset",
          mediaType: "file-audio", // Hardcoded for speech asset
          mediaContent: audioBase64,
        };

        const assetData = {
          AssetName: assetName,
          AssetType: "SpeechAsset",
          Caption: caption,
          AssetContent: JSON.stringify(preContent),
          PublicAsset: "private",              // Defaulting to private for simplicity
          BrandID: brandId,
        };

        const token = localStorage.getItem("authToken"); // Use authToken
        const response = await axios.post(
          "https://genz-staging.feeltiptop.com/api/asset/addAsset",
          assetData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        // Construct the new asset object for the list
        const newAsset = {
          assetId: response.data.asset_id,
          assetName: assetName,
          caption: caption,
          mediaType: "file-audio",
          mediaContent: audioBase64, // Include the base64 content for immediate use
        };

        onAssetCreated(newAsset); // Callback to update parent state
        setAssetName(""); // Reset form
        setCaption("");
        setError("");

      } catch (saveError) {
        console.error("Error creating asset:", saveError);
        const errorMsg = saveError.response?.data?.message || "Error saving asset.";
        setError(`Failed to save asset: ${errorMsg}`);
      } finally {
        setIsCreating(false); // Stop overall creating loading
      }
    } else {
      setError("Audio generation failed, cannot save asset.");
      setIsCreating(false);
    }
  };

  const handleUploadAndSave = async () => {
    if (!assetName.trim()) {
      setError("Please provide an Asset Name.");
      return;
    }
    if (isCreating) {
      setError("Asset creation in progress. Please wait.");
      return;
    }
    if (!selectedFile && !recordedBlob) {
      setError("Please upload or record an audio file.");
      return;
    }
    setError("");
    setIsCreating(true);

    let audioBase64;
    try {
      audioBase64 = await fileToWavBase64(recordedBlob || selectedFile);
    } catch (convErr) {
      console.error("Error converting audio to WAV:", convErr);
      setError("Failed to process audio file. Please try again.");
      setIsCreating(false);
      return;
    }

    // -------- Get or create Brand --------
    let brandId = null;
    try {
      const token = localStorage.getItem("authToken");
      const brandsResponse = await axios.get(
        "https://genz-staging.feeltiptop.com/api/brand/getUserBrands",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (Array.isArray(brandsResponse.data) && brandsResponse.data.length > 0) {
        brandId = Array.isArray(brandsResponse.data[0])
          ? brandsResponse.data[0][0]
          : brandsResponse.data[0].BrandID ?? brandsResponse.data[0];
      } else {
        const addResp = await axios.post(
          "https://genz-staging.feeltiptop.com/api/brand/addBrand",
          { BrandName: `${user} - Brand` },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        brandId = addResp.data.brand_id;
      }
    } catch (brandErr) {
      console.error("Error fetching/creating brand:", brandErr);
      setError("Failed to get or create Brand. Please try again.");
      setIsCreating(false);
      return;
    }

    try {
      const preContent = {
        assetType: "SpeechAsset",
        mediaType: "file-audio",
        mediaContent: audioBase64,
      };
      const assetData = {
        AssetName: assetName,
        AssetType: "SpeechAsset",
        Caption: caption,
        AssetContent: JSON.stringify(preContent),
        PublicAsset: "private",
        BrandID: brandId,
      };
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        "https://genz-staging.feeltiptop.com/api/asset/addAsset",
        assetData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newAsset = {
        assetId: response.data.asset_id,
        assetName: assetName,
        caption: caption,
        mediaType: "file-audio",
        mediaContent: audioBase64,
      };
      onAssetCreated(newAsset);
      setAssetName("");
      setSelectedFile(null);
      setRecordedBlob(null);
      setError("");
    } catch (saveErr) {
      console.error("Error creating asset:", saveErr);
      const errorMsg = saveErr.response?.data?.message || "Error saving asset.";
      setError(`Failed to save asset: ${errorMsg}`);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-md">
      <h3 className="mb-2 text-xl font-bold text-gray-800">Asset Creation</h3>
      <div className="mb-4 flex justify-center items-center space-x-4">
        <button
          onClick={() => { setMode("tts"); setError(""); }}
          className={`px-3 py-1 rounded ${mode==="tts" ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"}`}
          disabled={isCreating || ttsLoading}
        >
          TTS
        </button>
        <button
          onClick={() => { setMode("upload"); setError(""); }}
          className={`px-3 py-1 rounded ${mode==="upload" ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"}`}
          disabled={isCreating || ttsLoading}
        >
          Upload/Record
        </button>
      </div>
      {error && (
        <div className="mb-4 rounded border border-red-400 bg-red-100 p-2 text-sm text-red-700">
          {error}
        </div>
      )}
      <div className="space-y-3">
        <div>
          <label htmlFor="newAssetName" className="mb-1 block text-sm font-medium text-gray-700">
            Asset Name:
          </label>
          <input
            type="text"
            id="newAssetName"
            value={assetName}
            onChange={(e) => setAssetName(e.target.value)}
            placeholder="Enter a name for the asset"
            className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-blue-500 focus:ring-blue-500"
            disabled={isCreating}
          />
        </div>
        {mode === "tts" ? (
          <>
            <div>
              <label htmlFor="newCaption" className="mb-1 block text-sm font-medium text-gray-700">
                Caption (Text to Speak):
              </label>
              <textarea
                id="newCaption"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Enter the text to be converted to speech"
                rows="3"
                className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                disabled={isCreating}
              />
            </div>
            <button
              onClick={generateTTSAndSave}
              disabled={isCreating || ttsLoading || !assetName.trim() || !caption.trim()}
              className="w-full rounded-lg bg-green-500 px-4 py-2 text-sm font-medium text-white hover:bg-green-600 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              {isCreating ? (ttsLoading ? 'Generating Audio...' : 'Saving Asset...') : "Generate & Save Asset"}
            </button>
          </>
        ) : (
          <>
          <div>
                <label htmlFor="uploadCaption" className="mb-1 block text-sm font-medium text-gray-700">
                  Caption:
                </label>
                <textarea
                  id="uploadCaption"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="Enter a caption for the audio"
                  rows="2"
                  className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                  disabled={isCreating}
                />
              </div>
            <div className="flex justify-center items-center mb-3 space-x-4">
              <button
                onClick={() => setUploadMode("upload")}
                className={`px-2 py-1 rounded ${uploadMode==="upload" ? "bg-green-500 text-white" : "bg-gray-200 text-gray-700"}`}
                disabled={isCreating}
              >
                Upload File
              </button>
              <button
                onClick={() => setUploadMode("record")}
                className={`px-2 py-1 rounded ${uploadMode==="record" ? "bg-green-500 text-white" : "bg-gray-200 text-gray-700"}`}
                disabled={isCreating}
              >
                Record Audio
              </button>
              
            </div>
            {uploadMode === "upload" ? (
              <div
                className="border-2 border-dashed border-gray-300 p-6 text-center rounded cursor-pointer"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleFileDrop}
                onClick={() => document.getElementById("fileUpload").click()}
              >
                {selectedFile ? selectedFile.name : "Drag & drop an audio file here, or click to select (.wav, .mp3, or .m4a)"}
                <input
                  id="fileUpload"
                  type="file"
                  accept=".wav,.mp3,.m4a"
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={isCreating}
                />
              </div>
            ) : (
              <div className="flex justify-center">
                <button
                  onClick={toggleRecording}
                  disabled={isCreating}
                  className="rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-gray-400"
                >
                  {isRecording ? "Stop Recording" : "Start Recording"}
                </button>
                {recordedBlob && (
                  <audio
                    src={URL.createObjectURL(recordedBlob)}
                    controls
                    className="mt-2 h-8 w-full"
                    style={{ maxHeight: '32px' }}
                  />
                )}
              </div>
            )}
            <button
              onClick={handleUploadAndSave}
              disabled={isCreating || (!selectedFile && !recordedBlob)}
              className="w-full rounded-lg bg-green-500 px-4 py-2 text-sm font-medium text-white hover:bg-green-600 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              {isCreating ? "Saving Asset..." : "Upload & Save Asset"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default function CommaFeedConstruction() {
  const navigate = useNavigate();
  const [numTasks, setNumTasks] = useState(3);
  const [tasks, setTasks] = useState([]); // Initialize empty, will be set in useEffect
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // For general errors (fetching)
  const [doctorInfo, setDoctorInfo] = useState(null);
  const [verifyingDoctor, setVerifyingDoctor] = useState(true);

  // Fetch assets from the API
  useEffect(() => {
    verifyDoctorAccess();
    const fetchAssets = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem("authToken");
        if (!token) {
          throw new Error("Authentication token not found.");
        }
        const response = await axios.get(
          "https://genz-staging.feeltiptop.com/api/speech-assets",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.data.success) {
          setAssets(response.data.assets);
        } else {
          throw new Error(response.data.error || "Failed to fetch assets: Unexpected response format");
        }
      } catch (err) {
        setError(err.response?.data?.message || err.message || "An error occurred while fetching assets.");
        console.error("Error fetching assets:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAssets();
  }, []);

  const getAuthHeaders = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      "Content-Type": "application/json",
    },
  });

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    navigate("/login");
  };

  const verifyDoctorAccess = async () => {
    if (!localStorage.getItem("authToken")) {
      setVerifyingDoctor(false);
      console.error("Error verifying doctor status:", error);
      return;
    }

    try {
      const response = await axios.get(
        "https://genz-staging.feeltiptop.com/api/doctor/info",
        getAuthHeaders(),
      );

      if (response.data.success) {
        setDoctorInfo(response.data.doctor);
        setIsAuthorized(true);
        fetchPatients();
      }
    } catch (error) {
      console.error("Error verifying doctor status:", error);
    } finally {
      setVerifyingDoctor(false);
    }
  };

  // Regenerate tasks array when numTasks changes
  useEffect(() => {
    setTasks((prev) => {
      const newTasks = [];
      const maxTasks = Math.max(1, numTasks);
      for (let i = 1; i <= maxTasks; i++) {
        const existing = prev.find((t) => t.id === i);
        newTasks.push(existing || { id: i, asset: null });
      }
      return newTasks.slice(0, maxTasks);
    });
  }, [numTasks]);

  // Drag handlers
  const handleDragStart = (e, asset) => {
    try {
      e.dataTransfer.setData("application/json", JSON.stringify(asset));
    } catch (err) {
      console.error("Failed to stringify asset data for drag:", err, asset);
    }
  };
  const handleDragOver = (e) => {
    e.preventDefault();
  };
  const handleDrop = (e, taskId) => {
    e.preventDefault();
    try {
      const assetJson = e.dataTransfer.getData("application/json");
      if (!assetJson) return;
      const asset = JSON.parse(assetJson);
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, asset } : t)),
      );
    } catch (err) {
      console.error("Failed to parse dropped asset data:", err);
      setError("Failed to process dropped asset.");
    }
  };

  const handleRemoveAsset = (taskId) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, asset: null } : t)),
    );
  };

  const handleAssetCreated = (newAsset) => {
    setAssets((prevAssets) => [...prevAssets, newAsset]);
  };

  const handleContinue = () => {
    const emptyTasks = tasks.filter((task) => !task.asset);
    if (emptyTasks.length > 0) {
      setError("Please assign assets to all tasks before continuing.");
      setTimeout(() => setError(null), 5000);
      return;
    }
    setError(null);

    navigate("/feed/configure", {
      state: {
        tasks,
        feedType: "comma",
      },
    });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100 p-4">
        <div className="text-lg text-gray-600">Loading assets...</div>
      </div>
    );
  }

  if (error && !loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100 p-4">
        <div className="w-full max-w-md rounded-lg bg-red-100 p-6 text-center text-red-800 shadow">
          <p className="mb-2 text-xl font-semibold">Error, please login again</p>
          <p className="mb-4">{error}</p>
          <button
            onClick={() => navigate("/")}
            className="rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      {/* Navbar */}
      <nav className="flex items-center justify-between bg-white px-6 py-4 shadow">
        <div
          className="group relative flex items-center"
          style={{ minHeight: 80 }}
        >
          {/* Logo and Title */}
          <div
            className="duration-400 flex items-center transition-all ease-in-out"
            style={{}}
          >
            {/* Animated Back Button */}
            <button
              onClick={() => navigate("/CommaDashboard")}
              className="duration-400 pointer-events-none z-10 -translate-x-14 transform transition-all ease-in-out group-hover:pointer-events-auto group-hover:translate-x-0 group-hover:opacity-100"
            >
              <ArrowBack className="h-6 w-6 text-gray-600" />
            </button>
            <button
            onClick={() => navigate("/CommaDashboard")}
            >
              <img
                src={Logo}
                alt="COMMA logo"
                className="w-85 duration-400 h-20 transition-all ease-in-out group-hover:ml-5"
                style={{
                  transition: "margin-left 0.4s cubic-bezier(0.4,0,0.2,1)",
                }}
              />
            </button>
            <div className="duration-400 group-hover:mHei4 ml-4 text-2xl font-semibold text-gray-800 transition-all ease-in-out">
              Assessment Creation
            </div>
          </div>
          
        </div>
        <button
          onClick={handleContinue}
          disabled={tasks.some((task) => !task.asset)}
          className="rounded-lg bg-blue-600 px-6 py-3 text-white shadow transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400"
        >
          Continue to Feed Configuration
        </button>
          <div className="flex items-center space-x-4">
            <Avatar
              src={`https://api.dicebear.com/7.x/initials/svg?seed=${doctorInfo.firstName + " " + doctorInfo.lastName}`}
              alt={doctorInfo.firstName + " " + doctorInfo.lastName}
              sx={{ width: 50, height: 50 }} // MUI uses pixels
            />
            <span className="text-lg font-bold text-gray-700">
              Dr. {doctorInfo?.firstName} {doctorInfo?.lastName}
            </span>
            <button onClick={handleLogout}>
              <LogOut className="h-6 w-6 text-gray-600" />
            </button>
            {/* Animated Back Button */}
            <button
              onClick={() => navigate("/CommaDashboard")}
              className="duration-400 pointer-events-none z-10 -translate-x-14 transform opacity-0 transition-all"
            >
              <ArrowBack className="h-6 w-6 text-gray-600" />
            </button>
          </div>
      </nav>

      <div className="mt-2 mb-2 flex items-center justify-end md:justify-end px-2">
        {error && (
          <div className="rounded bg-red-100 p-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
        <div className="flex w-full flex-col gap-6 lg:w-1/3">
          <CreateSpeechAssetCard onAssetCreated={handleAssetCreated} />
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-md">
            <h2 className="mb-2 text-xl font-bold text-gray-800">Assessment Sequence</h2>
            <p className="mb-6 text-center text-gray-500 md:text-left">
              Build a assessment by creating or uploading assets and assigning them to the assessment in sequence. You can click and drag assets from the <b>Available Audio Assets</b> to the tasks below.
              <br />
            </p>
            <div className="flex items-center justify-center mb-4 space-x-3">
              <button
                onClick={() => setNumTasks((n) => Math.max(1, n - 1))}
                disabled={numTasks <= 1}
                className="rounded-full bg-gray-200 px-3 py-1 text-lg font-bold text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Decrease tasks"
                type="button"
              >
                –
              </button>
              <input
                id="numTasksInput"
                type="number"
                min="1"
                value={numTasks}
                onChange={(e) => setNumTasks(parseInt(e.target.value) || 1)}
                className="w-16 rounded border border-gray-300 px-2 py-1 text-center text-lg font-semibold appearance-none
                          [&::-webkit-outer-spin-button]:appearance-none
                          [&::-webkit-inner-spin-button]:appearance-none
                          [&::-webkit-outer-spin-button]:m-0
                          [&::-webkit-inner-spin-button]:m-0
                          [-moz-appearance:textfield]"
              />

              <button
                onClick={() => setNumTasks((n) => n + 1)}
                className="rounded-full bg-gray-200 px-3 py-1 text-lg font-bold text-gray-700 hover:bg-gray-300"
                aria-label="Increase tasks"
                type="button"
              >
                +
              </button>
            </div>
            <div className="space-y-3">
              {tasks.map((task) => (
                <div key={task.id} className="flex items-center justify-between space-x-2">
                  <span className="flex-shrink-0 font-semibold text-gray-700">Task {task.id}:</span>
                  <div
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, task.id)}
                    className={`flex h-12 flex-grow items-center justify-center rounded border-2 border-dashed p-2 text-sm transition-colors ${
                      task.asset ? 'border-green-300 bg-green-50' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    {task.asset ? (
                      <span className="truncate font-medium text-green-800" title={task.asset.caption}>
                        {task.asset.caption || task.asset.assetName || 'Unnamed Asset'}
                      </span>
                    ) : (
                      <span className="text-gray-500">Drop audio here</span>
                    )}
                  </div>
                  {task.asset && (
                    <button
                      onClick={() => handleRemoveAsset(task.id)}
                      className="ml-1 flex-shrink-0 rounded-full p-1 text-red-500 hover:bg-red-100"
                      title="Remove asset"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="w-full lg:w-2/3">
          <div className="h-[60vh] min-h-[400px] rounded-lg border border-gray-200 bg-white p-4 shadow-md lg:h-[calc(80vh)]">
            <h3 className="mb-2 text-xl font-bold text-gray-800">Available Audio Assets</h3>
            {assets.length === 0 ? (
              <p className="mt-10 text-center text-gray-500">
                No assets available. You can create one using the <b>Asset Creation</b> form on the left.
              </p>
            ) : (
              <>
              <p className="text-center text-gray-500">
                You can create assets using the <b>Asset Creation</b> form on the left.
              </p>
              <div className="grid h-[calc(100%-4rem)] grid-cols-1 gap-4 overflow-y-auto pr-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                
                {assets.map((asset) => (
                  <div
                    key={asset.assetId}
                    draggable
                    onDragStart={(e) => handleDragStart(e, asset)}
                    className="flex h-fit cursor-move flex-col rounded-md border border-gray-200 bg-gray-50 p-3 shadow-sm transition-shadow hover:shadow-md"
                    title={`Name: ${asset.assetName}
Caption: ${asset.caption}`}
                  >
                    <p className="mb-1 truncate text-sm font-semibold text-gray-800">{asset.assetName}</p>
                    <p className="mb-2 line-clamp-2 flex-grow text-xs text-gray-600">{asset.caption}</p>
                    {asset.mediaContent && asset.mediaType === "file-audio" && (
                      <audio
                        src={asset.mediaContent}
                        controls
                        className="mt-auto h-8 w-full"
                        style={{ maxHeight: '32px' }}
                      />
                    )}
                  </div>
                ))}
              </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="mt-8 flex flex-col items-center justify-end gap-4 md:flex-row">
        {error && (
          <div className="rounded bg-red-100 p-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
